import * as React from "react";
import { FunctionComponent } from "react";
import { Alert, Button, Col, Grid, Row } from "react-bootstrap";
import { Redirect } from "react-router";
import {
  BrowserRouter,
  Route,
  RouteComponentProps,
  Switch,
} from "react-router-dom";
import affiliates, { buildRedirectUrl } from "./affiliates";
import asyncComponent from "./components/AsyncComponent";
import { Protected } from "./components/Protected";
import SurveyLoader from "./components/SurveyLoader";
import ThanksPage from "./components/ThanksPage";
import { StartAuthCode } from "./views/StartAuthCode";
import { StartAuthCodeStatic } from "./views/StartAuthCodeStatic";
// import { StartNoCode } from "./views/StartNoCode";
import { StartNoCodeStatic } from "./views/StartNoCodeStatic";
import Success from "./views/Success";

import "bootstrap/dist/css/bootstrap.min.css";
import "./global.css";
import LogoLink from "./components/LogoLink.tsx";

interface RouteObject {
  path?: string;
  protected?: boolean;
  component: React.ComponentType<any>;
}

interface AppState {
  hasError: boolean;
}

const WildcardComponent: FunctionComponent<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const path = props.location.pathname;

  const a = affiliates.get(path.toLowerCase());
  if (a) {
    // Redirect with UTM parameters instead of setting cookie
    const redirectUrl = buildRedirectUrl(a);
    return <Redirect to={redirectUrl} />;
  }
  return <Alert bsStyle="warning">404 Not Found.</Alert>;
};

// const renderNoMatch = () => <Alert bsStyle="warning">Not found</Alert>;
const AsyncAdmin = asyncComponent(() => import("./views/Admin"));
const AsyncLogin = asyncComponent(() => import("./views/Login"));
const AsyncAppLogs = asyncComponent(() => import("./views/Logs"));

class App extends React.Component<Record<string, never>, AppState> {
  state: AppState = { hasError: false };

  public render() {
    if (location.hash.startsWith("#/")) {
      const dest = location.hash.replace("#", "");
      // alert(dest);
      window.history.replaceState(null, "wut", dest); // or history.replace
    }
    return (
      <BrowserRouter>
        <Switch>
          {this.renderAdminRoutes()}
          {/* This should always be last because it contains no-match route: */}
          {this.renderPublicRoutes()}
        </Switch>
      </BrowserRouter>
    );
  }

  // private readonly noEmail = ["protege-spouse", "protege"];

  private readonly publicRoutes: RouteObject[] = [
    // is there a better way to do this kind of redirect?
    {
      path: "/mentor-spouse",
      component: () => {
        return <Redirect to={`/mentor`} />;
      },
    },
    // {
    //   path: `/:surveyDefinitionUrl(${this.noEmail.join("|")})`,
    //   component: StartNoCode,
    // },
    // {
    //   path: `/:surveyDefinitionUrl(${this.noEmail.join("|")})/:sp`,
    //   component: StartNoCode,
    // },

    {
      path: "/mentor-plain",
      component: StartAuthCode,
    },
    {
      // Special short-link for https://dvsv3.com/
      path: "/v3",
      component: (props: RouteComponentProps) => (
        <StartAuthCodeStatic code={"acpv3"} {...props} />
      ),
    },
    {
      path: "/mentor/:code?",
      component: (props: RouteComponentProps<{ code?: string }>) => (
        <StartAuthCodeStatic code={props.match.params.code} {...props} />
      ),
    },
    {
      path: "/protege",
      component: (props: RouteComponentProps) => (
        <StartNoCodeStatic surveyDefinitionUrl="protege" {...props} />
      ),
    },
    {
      path: "/protege-spouse",
      component: (props: RouteComponentProps) => (
        <StartNoCodeStatic surveyDefinitionUrl="protege-spouse" {...props} />
      ),
    },

    {
      path: "/thanks/:type/:applicationId",
      component: ThanksPage,
    },
    {
      path: "/a/:uuid/:pane",
      component: SurveyLoader,
    },
    {
      path: "/a/:uuid",
      component: SurveyLoader,
    },
    {
      path: "/success",
      component: Success,
    },
    {
      path: "/login",
      component: AsyncLogin,
    },
    {
      path: "/returning-participant",
      component: () => {
        return (
          <div>
            <Row
              style={{
                paddingBottom: "1em",
                background: "#fff",
              }}
            >
              <Col xs={12}>
                <h2 className="pull-left">
                  Application Exists For This Email Address
                </h2>
                <LogoLink />
              </Col>
              <Col xs={12}>
                <p>
                  Thank you for your interest in ACP mentorship! An application
                  is already on file for this address. An ACP representative
                  will reach out to you within one business day to discuss the
                  next steps.
                </p>

                <p className="alert alert-info">
                  <strong>IMPORTANT:</strong> There is no need to submit an
                  additional application at this stage. If you don't hear from
                  an ACP representative within one business day, please check
                  your spam folder and, if you still don't see an email from us,
                  you're welcome to give us a call at (212) 752-0700.
                </p>
              </Col>
            </Row>
          </div>
        );
      },
    },
    {
      path: "/affiliate-cheatsheet",
      component: () => {
        const [copiedUrl, setCopiedUrl] = React.useState<string | null>(null);

        const copyToClipboard = async (url: string) => {
          try {
            await navigator.clipboard.writeText(url);
            setCopiedUrl(url);
            setTimeout(() => setCopiedUrl(null), 2000);
          } catch (err) {
            console.error("Failed to copy:", err);
          }
        };

        return (
          <div>
            <table className="data-table">
              <thead>
                <tr>
                  <th> </th>
                  <th>URL</th>
                  <th>Referrer</th>
                  <th>Destination</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(affiliates).map(([path, affiliate]) => {
                  const url = `https://apply.acp-usa.org${path}`;
                  return (
                    <tr key={path}>
                      <td>
                        <Button
                          bsSize="xsmall"
                          bsStyle={copiedUrl === url ? "success" : "default"}
                          onClick={() => copyToClipboard(url)}
                        >
                          {copiedUrl === url ? "Copied!" : "Copy"}
                        </Button>
                      </td>
                      <td style={{ paddingRight: "0.75em" }}>
                        <a href={url}>{url}</a>
                      </td>
                      <td>{affiliate.referrer}</td>
                      <td>{affiliate.applicationPath}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      },
    },
    // Handy for testing error handling.  Notice the setState() hack used inside an event handler.  This allows our
    // thrown error to be caught and handled by the ErrorBoundary component.  React Error Boundaries don't usually
    // catch stuff thrown in event handlers (see the docs for why that is).
    {
      path: "/force-error",
      component: () => {
        return (
          <div style={{ textAlign: "center", padding: 50 }}>
            <p>
              Experiencing{" "}
              <a
                target="callvoid"
                href="http://bearlamp.com.au/the-call-of-the-void/"
              >
                L'appel du vide
              </a>
              ?
            </p>
            <div style={{ padding: 12 }}>
              <Button
                bsStyle="danger"
                onClick={() => {
                  this.setState(() => {
                    throw new Error("You broke it on purpose");
                  });
                }}
              >
                Break Things
              </Button>
            </div>
            <small>
              Clicking the button will cause an error. It won't damage anything.
              It's just a way to test how this app responds to unexpected
              stimuli. You know you want to...
            </small>
          </div>
        );
      },
    },
    {
      path: "/dump-env",
      component: () => {
        return (
          <table className="table table-striped">
            {Object.entries(import.meta.env).map(([k, v]) => (
              <tr>
                <th>{k}</th>
                <td>{JSON.stringify(v)}</td>
              </tr>
            ))}
          </table>
        );
        // return (<div>{JSON.stringify(import.meta.env)}</div>)
      },
    },
    {
      path: "/:something",
      component: WildcardComponent,
    },
    {
      path: "/",
      component: () => {
        window.location.href =
          import.meta.env.VITE_ORG_SITE_PROGRAM_PAGE ||
          "https://www.acp-usa.org/";
        return <div />;
      },
    },
  ];

  private readonly adminRoutes: RouteObject[] = [
    {
      path: "/admin/logs/:appId",
      component: Protected(AsyncAppLogs),
    },
    {
      path: "/admin",
      component: Protected(AsyncAdmin),
    },
  ];

  private adminLayout = (
    C: React.ComponentType<RouteComponentProps<any>>,
    props: RouteComponentProps<any>,
  ) => {
    return (
      <Grid
        style={{
          background: "white",
          maxWidth: "1400px",
          minWidth: "1200px",
          border: "1px solid #666",
          padding: "0 15px 14px 15px",
        }}
      >
        <C {...props} />
      </Grid>
    );
  };

  private renderAdminRoutes() {
    return this.adminRoutes.map((route, ind) => {
      return (
        <Route
          key={`public-${ind}`}
          path={route.path}
          render={(props: any) => this.adminLayout(route.component, props)}
        />
      );
    });
  }

  private publicLayout = (
    C: React.ComponentType<RouteComponentProps<any>>,
    props: RouteComponentProps<any>,
  ) => {
    return (
      <Grid
        style={{
          background: "white",
          maxWidth: "730px",
          border: "1px solid #666",
          padding: "0 15px 14px 15px",
        }}
      >
        <C {...props} />
      </Grid>
    );
  };

  private renderPublicRoutes() {
    return this.publicRoutes.map((route, ind) => {
      // Don't wrap Shadow DOM components in the Grid layout
      const isStaticComponent =
        route.path === "/protege" ||
        route.path === "/protege-spouse" ||
        route.path === "/v3" ||
        route.path === "/mentor/:code?";
      return (
        <Route
          key={`admin-${ind}`}
          path={route.path}
          render={(props) =>
            isStaticComponent ? (
              <route.component {...props} />
            ) : (
              this.publicLayout(route.component, props)
            )
          }
        />
      );
    });
  }
}

export default App;
